import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  Sparkle, Camera, Microphone, FileXls, ShareNetwork, ArrowRight,
  X as XIcon,
} from "@phosphor-icons/react";

/**
 * WelcomeTour (v3.7) — five dismissible cards shown to the user once,
 * the first time they reach the Dashboard after onboarding. Each card
 * deep-links into the relevant guide section so a curious user can
 * read more in two clicks.
 *
 * Persistence: `localStorage.arbor_tour_v1_dismissed = "true"` once the
 * user finishes or dismisses. Tied to the user_id so the same browser
 * shared by two users still shows each their first run.
 */
const STORAGE_KEY = "arbor_tour_v1_dismissed";

const STEPS = [
  {
    icon: Sparkle,
    eyebrow: "Welcome",
    title: "A homeowner's manual for the ledger itself.",
    body: "The Asset Arbor records two truths about every item you own: its financial value and the knowledge needed to take care of it. This brief tour points out the four most useful starting places.",
    anchor: "what-this-is",
  },
  {
    icon: Camera,
    eyebrow: "Step 1 · Recording",
    title: "Six ways to record an asset.",
    body: "Type it. Photograph it (and let Leo draft the record). Speak it. Scan a model plate. Drop a document. Drag in a spreadsheet. All six paths arrive at the same canonical record.",
    anchor: "six-doors",
  },
  {
    icon: Microphone,
    eyebrow: "Step 2 · Leo",
    title: "A private estate analyst lives in the sidebar.",
    body: "Leo notices clusters in your collection, drafts the weekly digest, and answers questions about your ledger. He never sees outside it.",
    anchor: "leo",
  },
  {
    icon: FileXls,
    eyebrow: "Step 3 · Migrating",
    title: "Bring your spreadsheet with you.",
    body: "Already have an inventory in Excel or a CSV from another tool? The Spreadsheet ingest reads up to 500 rows, lets Leo propose new classes, and lets you confirm row by row before committing.",
    anchor: "six-doors",
  },
  {
    icon: ShareNetwork,
    eyebrow: "Step 4 · For your people",
    title: "Share with heirs on your terms.",
    body: "Generate a read-only link. Decide for each link whether valuation, dispensation, and photos are visible. Revoke at any time. The Estate Manifest PDF is the printable companion piece.",
    anchor: "heirs",
  },
];

export default function WelcomeTour() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    try {
      const key = `${STORAGE_KEY}_${user.user_id || user.id || "anon"}`;
      if (!localStorage.getItem(key)) {
        // Show after a beat so the dashboard renders first
        const t = setTimeout(() => setOpen(true), 600);
        return () => clearTimeout(t);
      }
    } catch {
      // localStorage blocked — just skip the tour
    }
  }, [user]);

  const dismiss = () => {
    try {
      const key = `${STORAGE_KEY}_${user?.user_id || user?.id || "anon"}`;
      localStorage.setItem(key, "true");
    } catch {
      /* ignore */
    }
    setOpen(false);
  };

  const next = () => {
    if (step < STEPS.length - 1) setStep(step + 1);
    else dismiss();
  };

  const openGuide = (anchor) => {
    dismiss();
    navigate(`/help#${anchor}`);
  };

  if (!open || !user) return null;

  const current = STEPS[step];
  const Icon = current.icon;

  return (
    <div
      data-testid="welcome-tour"
      className="fixed inset-0 z-[80] flex items-center justify-center bg-[#0A192F]/40 p-4"
      onClick={dismiss}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid={`welcome-tour-step-${step + 1}`}
        className="bg-white border border-[#0F2818] max-w-lg w-full shadow-2xl"
      >
        <div className="bg-[#1A3622] px-5 py-3 flex items-center justify-between text-[#F9F8F6]">
          <div className="flex items-center gap-2 font-mono-ledger text-[10px] uppercase tracking-[0.25em]">
            <Icon size={12} weight="fill" color="#C5A059" />
            {current.eyebrow}
          </div>
          <button
            onClick={dismiss}
            aria-label="Skip tour"
            data-testid="welcome-tour-skip"
            className="text-[#F9F8F6]/70 hover:text-[#F9F8F6]"
          >
            <XIcon size={14} />
          </button>
        </div>
        <div className="p-6">
          <h3 className="font-serif-display text-3xl text-[#0A192F] leading-tight">
            {current.title}
          </h3>
          <p className="text-sm text-[#5C5F58] mt-4 leading-relaxed">{current.body}</p>
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-[#E0DDD6]">
            <div className="flex gap-1.5" data-testid="welcome-tour-dots">
              {STEPS.map((_, i) => (
                <span
                  key={i}
                  className={`w-1.5 h-1.5 ${i === step ? "bg-[#1A3622]" : "bg-[#E0DDD6]"}`}
                />
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => openGuide(current.anchor)}
                data-testid="welcome-tour-read"
                className="text-xs text-[#5C5F58] hover:text-[#1A3622] underline underline-offset-2"
              >
                Read more
              </button>
              <button
                onClick={next}
                data-testid="welcome-tour-next"
                className="inline-flex items-center gap-2 bg-[#1A3622] text-[#F9F8F6] px-4 py-2 text-sm border border-[#0F2818] hover:bg-[#0F2818]"
              >
                {step < STEPS.length - 1 ? "Next" : "Begin"}
                <ArrowRight size={12} weight="bold" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
